import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSupportContext } from '../../context/SupportContext';
import { useI18n } from '../../context/I18nContext';
import { useReportData, VALUE_SOURCE } from '../../context/ReportDataContext';
import { useDisputeTray, DISPUTE_CATEGORIES } from '../../context/DisputeTrayContext';
import { useTheme } from '../../layouts/SelfServePortalLayout';
import axios from 'axios';
import { cn } from '../../lib/utils';
import {
  FileText,
  AlertTriangle,
  CheckCircle,
  Check,
  Sparkles,
  Search,
  CreditCard,
  Building,
  TrendingUp,
  TrendingDown,
  Eye,
  Plus,
  Minus,
  RefreshCw,
  User,
  FileSearch,
  AlertCircle,
  ChevronDown,
  Info,
  Shield,
  Database,
  Phone,
  MapPin,
  Clock,
  Filter,
  Scale,
  ShieldAlert,
  ShoppingCart,
  Send,
  Upload,
  Key,
  Globe,
  Edit,
} from 'lucide-react';
import { ScoreGauge, SCORE_MIN, SCORE_MAX } from '../../components/ui/ScoreGauge';
import { BureauLogo } from '../../components/ui/BureauLogo';
import { getAccountSeverity, getPrimaryDisplayTag, isNegativeAccount, logNegativeClassificationDebug } from '../../utils/negativeAccountClassifier';
import { getDisputeEligibility, needsCautionWarning } from '../../utils/disputeEligibility';
import {
  getTradelineReportedBureaus,
  tradelineMatchesBureauFilter,
  tradelineReportsBureau,
} from '../../utils/tradelineBureaus';
import { PositiveAccountWarning } from '../../components/disputes/PositiveAccountWarning';
import RequestReportModal from '../../components/disputes/RequestReportModal';
import ReimportModal from '../../components/credit-report/ReimportModal';
import CredentialImportStatusCardBoundary from '../../components/credit-report/CredentialImportStatusCardBoundary';
import ReportDateLines from '../../components/credit-report/ReportDateLines';
import ProviderRefreshImportDialog from '../../components/import/ProviderRefreshImportDialog';
import {
  getImportStartErrorMessage,
  startSavedCredentialReportImport,
} from '../../hooks/useProviderRefreshImport';
import {
  markAutoRefreshDismissed,
  markRefreshHandledAfterCompletion,
} from '../../utils/creditReportAutoRefreshGuard';
import {
  creditReportPathForTab,
  creditReportTabFromPathname,
} from '../../utils/creditReportRoutes';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

// Provider display name mapping
const PROVIDER_DISPLAY_NAMES = {
  identityiq: 'IdentityIQ',
  credit_hero_score: 'CreditHeroScore',
  smartcredit: 'SmartCredit',
  myscoreiq: 'MyScoreIQ',
  privacyguard: 'PrivacyGuard',
  creditkarma: 'Credit Karma',
  experian: 'Experian',
  equifax: 'Equifax',
  transunion: 'TransUnion',
};

const normalizeBureauKey = (value) => {
  const compact = String(value || '').toLowerCase().replace(/[^a-z]/g, '');
  if (!compact) return null;
  if (compact.includes('transunion') || compact === 'tu') return 'transunion';
  if (compact.includes('experian') || compact === 'exp') return 'experian';
  if (compact.includes('equifax') || compact === 'efx') return 'equifax';
  return null;
};

/**
 * Normalize report origin from various backend field formats
 * @param {Object} report - Report object
 * @returns {Object} Normalized origin { method, provider, fileType, confidence }
 */
const normalizeReportOrigin = (report) => {
  if (!report) return { method: null, provider: null, fileType: null, confidence: null };
  
  const importSource = report.import_source?.toLowerCase() || '';
  const importProvider = report.import_provider?.toLowerCase() || '';
  const fileType = report.file_type?.toLowerCase() || '';
  const confidence = report.provider_confidence || null;
  
  // Determine method from import_source and file_type
  let method = null;
  if (importSource === 'playwright_import' || importSource === 'credential_import' || 
      importSource === 'browser_import' || fileType === 'imported_html') {
    method = 'credential_import';
  } else if (importSource === 'uploaded_pdf' || importSource === 'pdf_upload' || 
             fileType === 'pdf' || fileType === 'uploaded_pdf') {
    method = 'uploaded_pdf';
  } else if (importSource === 'uploaded_html' || importSource === 'html_upload' || 
             fileType === 'html' || fileType === 'uploaded_html') {
    method = 'uploaded_html';
  } else if (importSource === 'manual_entry' || importSource === 'manual') {
    method = 'manual_entry';
  } else if (importSource === 'api_import' || importSource === 'api') {
    method = 'api_import';
  } else if (importSource === 'upload' || importSource === 'uploaded' || importSource === 'file_upload') {
    method = 'uploaded';
  }
  
  // Normalize provider name
  const provider = importProvider ? (PROVIDER_DISPLAY_NAMES[importProvider] || importProvider) : null;
  
  return {
    method,
    provider,
    fileType: fileType || null,
    confidence,
  };
};

/**
 * Determine if provider should be shown based on confidence and method
 */
const shouldShowProvider = (origin) => {
  if (!origin || !origin.provider) return false;
  
  // Always show for credential import - provider is explicitly known
  if (origin.method === 'credential_import') return true;
  
  // Always show for API import - provider is explicitly known
  if (origin.method === 'api_import') return true;
  
  // For uploads, only show if confidence is high
  if ((origin.method === 'uploaded_pdf' || origin.method === 'uploaded_html') && 
      origin.confidence === 'high') {
    return true;
  }
  
  return false;
};

const CR = 'creditReport';

/**
 * Get source label for banner (no prefix)
 */
const getBannerSourceLabel = (origin, t) => {
  if (!origin?.method) return t(`${CR}.bannerImportedReport`);

  const showProvider = shouldShowProvider(origin);
  const provider = origin.provider;

  switch (origin.method) {
    case 'credential_import':
      return showProvider
        ? t(`${CR}.bannerCredentialImportProvider`, { provider })
        : t(`${CR}.bannerCredentialImport`);
    case 'uploaded_pdf':
      return showProvider
        ? t(`${CR}.bannerUploadedPdfProvider`, { provider })
        : t(`${CR}.bannerUploadedPdfReport`);
    case 'uploaded_html':
      return showProvider
        ? t(`${CR}.bannerUploadedHtmlProvider`, { provider })
        : t(`${CR}.bannerUploadedHtmlReport`);
    case 'manual_entry':
      return t(`${CR}.bannerManualCreditReportEntry`);
    case 'api_import':
      return showProvider
        ? t(`${CR}.bannerApiImportProvider`, { provider })
        : t(`${CR}.bannerApiImport`);
    case 'uploaded':
      return showProvider
        ? t(`${CR}.bannerUploadedReportProvider`, { provider })
        : t(`${CR}.bannerUploadedReport`);
    default:
      return t(`${CR}.bannerImportedReport`);
  }
};

/**
 * Get compact source label for bureau card badges
 */
const getBureauBadgeLabel = (origin, t) => {
  if (!origin?.method) return t(`${CR}.badgeImported`);

  switch (origin.method) {
    case 'credential_import':
      return t(`${CR}.badgeCredentialImport`);
    case 'uploaded_pdf':
      return t(`${CR}.badgeUploadedPdf`);
    case 'uploaded_html':
      return t(`${CR}.badgeUploadedHtml`);
    case 'manual_entry':
      return t(`${CR}.badgeManualEntry`);
    case 'api_import':
      return t(`${CR}.badgeApiImport`);
    case 'uploaded':
      return t(`${CR}.badgeUploaded`);
    default:
      return t(`${CR}.badgeImported`);
  }
};

/**
 * Legacy function for backwards compatibility - Get full source display object
 */
const getReportSourceDisplay = (report, t) => {
  const origin = normalizeReportOrigin(report);

  let icon = FileText;
  if (origin.method === 'credential_import') icon = Key;
  else if (origin.method === 'uploaded_pdf' || origin.method === 'uploaded_html' || origin.method === 'uploaded') icon = Upload;
  else if (origin.method === 'manual_entry') icon = Edit;
  else if (origin.method === 'api_import') icon = Globe;

  return {
    label: getBannerSourceLabel(origin, t),
    icon,
    method: origin.method || 'unknown',
    provider: origin.provider,
    origin,
  };
};

import { toast } from 'sonner';
import { AccountDetailsPanel, PaymentHistoryGrid, BureauComparisonSection, InquiriesTable, PublicRecordsTable, PersonalInfoTable, ReportHistorySelector } from '../../components/credit-report';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Credit Report Page Theme Tokens
 * Reference: FundElite Credit Report - Improved Preview HTML
 */
const getCreditReportTheme = (isDark) => ({
  isDark,
  
  // Core backgrounds
  bg: {
    bg0: isDark ? '#081610' : '#F7F9F8',
    bg1: isDark ? '#0B1F18' : '#F2FCF7',
    bg2: isDark ? '#0F2A21' : '#ECF8F1',
  },
  
  // Text colors
  text: {
    primary: isDark ? '#EAF7F0' : '#111827',
    secondary: isDark ? '#C7DDD0' : '#4B5563',
    muted: isDark ? '#93AE9F' : '#6B7280',
    danger: isDark ? '#F27A7A' : '#EF4444',
  },
  
  // Icon color mapping (key system from reference)
  icon: {
    neutral: isDark ? '#9DB3A7' : '#5F6F64',
    primary: isDark ? '#56B988' : '#0D9F64',
    warn: '#EAB23A',
    danger: '#EF4444',
  },
  
  // Surface colors
  surface: {
    s1: isDark ? 'rgba(7,28,21,0.62)' : 'rgba(255,255,255,0.84)',
    s2: isDark ? 'rgba(10,34,25,0.58)' : 'rgba(255,255,255,0.76)',
    s3: isDark ? 'rgba(12,38,28,0.54)' : 'rgba(255,255,255,0.66)',
  },
  
  // Border colors
  border: {
    b1: isDark ? 'rgba(142,218,182,0.12)' : 'rgba(255,255,255,0.44)',
    b2: isDark ? 'rgba(142,218,182,0.10)' : 'rgba(15,172,108,0.12)',
    soft: isDark ? 'rgba(142,218,182,0.10)' : 'rgba(15,172,108,0.10)',
  },
  
  // Card surfaces
  card: {
    bg: isDark ? 'rgba(7,28,21,0.62)' : 'rgba(255,255,255,0.84)',
    border: isDark ? 'rgba(142,218,182,0.12)' : 'rgba(255,255,255,0.44)',
    shadow: isDark ? '0 18px 48px rgba(0,0,0,0.26)' : '0 14px 36px rgba(16,24,40,0.08)',
    innerGlow: isDark 
      ? 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0))' 
      : 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0))',
  },
  
  // Input/filter surfaces
  input: {
    bg: isDark ? 'rgba(10,34,25,0.54)' : 'rgba(255,255,255,0.72)',
    border: isDark ? 'rgba(142,218,182,0.10)' : 'rgba(15,172,108,0.12)',
    text: isDark ? '#EAF7F0' : '#111827',
    placeholder: isDark ? '#6B8878' : '#9CA3AF',
  },
  
  // Table surfaces (matching reference exactly)
  table: {
    container: isDark ? 'rgba(7,28,21,0.62)' : 'rgba(255,255,255,0.84)',
    header: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.60)',
    row: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.52)',
    rowAlt: isDark ? 'rgba(255,255,255,0.045)' : 'rgba(247,252,249,0.74)',
    rowHover: isDark ? 'rgba(142,218,182,0.06)' : 'rgba(15,172,108,0.04)',
    border: isDark ? 'rgba(142,218,182,0.10)' : 'rgba(15,172,108,0.10)',
  },
  
  // Flow bar / tab bar
  tabBar: {
    bg: 'linear-gradient(135deg, #063D2D 0%, #08754A 45%, #0D9F64 100%)',
    border: 'rgba(15,172,108,0.16)',
    shadow: '0 14px 32px rgba(6,61,45,0.14)',
    activeBg: 'rgba(255,255,255,0.94)',
    activeText: '#1F2937',
    inactiveText: '#EAF7F0',
    chevronOpacity: 0.56,
  },
  
  // Green accent colors
  green: {
    500: '#0FAC6C',
    600: '#0D9F64',
    700: '#0B8C58',
    800: '#08754A',
    900: '#063D2D',
  },
  
  // Tag colors (from reference)
  tag: {
    red: { bg: 'rgba(239,68,68,0.12)', color: '#F27A7A' },
    gold: { bg: 'rgba(234,178,58,0.16)', color: '#F0C160' },
    green: { bg: 'rgba(15,172,108,0.12)', color: '#7FD7A8' },
  },
  
  // Source badge (PDF Source, Imported, Derived)
  sourceBadge: {
    bg: isDark ? 'rgba(15,172,108,0.14)' : 'rgba(15,172,108,0.10)',
    border: isDark ? 'rgba(15,172,108,0.18)' : 'rgba(15,172,108,0.14)',
    color: isDark ? '#8FE0B7' : '#0B8C58',
  },
  
  // Status pill
  statusPill: {
    bg: isDark ? 'rgba(15,172,108,0.18)' : 'rgba(15,172,108,0.12)',
    color: isDark ? '#9AE3BF' : '#0B8C58',
    neutral: {
      bg: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)',
      color: isDark ? '#C7DDD0' : '#4B5563',
    },
  },
  
  // Bureau pill
  bureauPill: {
    bg: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
    border: isDark ? 'rgba(142,218,182,0.10)' : 'rgba(15,172,108,0.12)',
    color: isDark ? '#C7DDD0' : '#4B5563',
  },
  
  // Check box
  checkBox: {
    bg: 'rgba(86,185,136,0.16)',
    border: 'rgba(86,185,136,0.36)',
    color: '#8DE5B7',
  },
  
  // Icon box
  iconBox: {
    bg: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.10)',
    border: isDark ? 'rgba(142,218,182,0.10)' : 'rgba(15,172,108,0.12)',
    color: isDark ? '#56B988' : '#0D9F64',
    warn: { bg: 'rgba(239,68,68,0.08)', color: '#EF4444' },
  },
  
  // Radio selection
  radio: { border: 'rgba(86,185,136,0.82)' },
  
  // Action colors
  action: {
    primary: '#62D09B',
    secondary: isDark ? '#C7DDD0' : '#4B5563',
  },
  
  // Positive box / empty state
  positiveBox: {
    bg: isDark ? 'rgba(15,172,108,0.12)' : 'rgba(15,172,108,0.10)',
    border: 'rgba(15,172,108,0.16)',
    color: isDark ? '#8FE0B7' : '#0B8C58',
  },
  
  // Empty state icon
  emptyIcon: {
    bg: 'rgba(15,172,108,0.10)',
    border: isDark ? 'rgba(142,218,182,0.10)' : 'rgba(15,172,108,0.12)',
    color: isDark ? '#56B988' : '#0D9F64',
  },
  
  // Metric card
  metric: {
    value: isDark ? '#EAF7F0' : '#111827',
    label: isDark ? '#93AE9F' : '#6B7280',
  },
  
  // Contact card
  contact: {
    title: isDark ? '#EAF7F0' : '#111827',
    meta: isDark ? '#93AE9F' : '#6B7280',
  },
  
  // Gauge theme
  gauge: { baseStroke: 'rgba(189,198,211,0.42)' },
  
  // Pill colors for scores
  pill: {
    poor: { bg: 'rgba(239,68,68,0.14)', color: '#E45757' },
    fair: { bg: 'rgba(245,158,11,0.14)', color: '#EAB23A' },
    good: { bg: 'rgba(15,172,108,0.14)', color: '#0B8C58' },
    excellent: { bg: 'rgba(15,172,108,0.18)', color: '#0FAC6C' },
  },
  
  // Shield icon
  shield: {
    bg: 'rgba(15,172,108,0.12)',
    color: isDark ? '#56B988' : '#0D9F64',
  },
});

const getMainFilters = (t) => [
  { value: 'all', label: t('creditReport.allAccounts') },
  { value: 'negative', label: t('creditReport.filterNegative') },
  { value: 'disputable', label: t('creditReport.filterDisputable') },
  { value: 'collections', label: t('creditReport.filterCollections') },
  { value: 'chargeoffs', label: t('creditReport.filterChargeoffs') },
  { value: 'late_payments', label: t('creditReport.filterLatePayments') },
  { value: 'open', label: t('creditReport.filterOpenAccounts') },
  { value: 'closed', label: t('creditReport.filterClosedAccounts') },
];

// Helper to determine score status based on value
const getScoreStatus = (score) => {
  if (!score || score === 0) return { label: '--', color: '#6B7280' };
  if (score >= 750) return { label: 'Very Good', color: '#0FAC6C' };
  if (score >= 700) return { label: 'Good', color: '#56B988' };
  if (score >= 650) return { label: 'Fair', color: '#EAB23A' };
  return { label: 'Poor', color: '#EF4444' };
};

// Bureau Score Card Component - Matching reference HTML layout exactly
const BureauScoreCard = ({ bureau, data, theme, reportOrigin, strictDerogatoryCount = 0, report = null }) => {
  const { t } = useI18n();
  // Score can be: number (719), object ({ value: 719, source: 'explicit' }), or null
  const rawScore = data?.score;
  const score = typeof rawScore === 'object' && rawScore !== null ? rawScore.value : rawScore;
  // Use the normalized report origin for consistent source labeling
  const sourceLabel = getBureauBadgeLabel(reportOrigin, t);

  const isUnavailable = !data
    || (Object.keys(data).length === 0)
    || isPlaceholderCreditScore(score)
    || (data.score_source === 'no_data' || data.score_source === 'fallback')
    || (data.score === null && (data.total_accounts === undefined || data.total_accounts === 0));

  const [displayScore, setDisplayScore] = useState(score || 0);
  
  // Get status pill style
  const getScorePill = (s) => {
    if (!s || s === 0) return { label: '--', style: theme.pill.poor };
    if (s >= 750) return { label: t('dashboard.excellent'), style: theme.pill.excellent };
    if (s >= 700) return { label: t('dashboard.good'), style: theme.pill.good };
    if (s >= 650) return { label: t('dashboard.fair'), style: theme.pill.fair };
    return { label: t('dashboard.poor'), style: theme.pill.poor };
  };
  
  const pill = getScorePill(score);
  
  const handleScoreUpdate = useCallback((animatedScore) => {
    setDisplayScore(animatedScore);
  }, []);

  return (
    <div 
      className="relative overflow-hidden"
      style={{
        background: theme.card.bg,
        border: `1px solid ${theme.card.border}`,
        borderRadius: '18px',
        boxShadow: theme.card.shadow,
        backdropFilter: 'blur(16px) saturate(140%)',
        WebkitBackdropFilter: 'blur(16px) saturate(140%)',
      }}
      data-testid={`bureau-card-${bureau.toLowerCase()}`}
    >
      {/* Responsive padding: tighter on mobile, normal on desktop */}
      <div className="px-2 py-2 sm:px-3 sm:py-2.5">
      {/* Inner glow overlay */}
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          background: theme.card.innerGlow,
          pointerEvents: 'none',
        }}
      />
      
      {/* Card content */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header row with badge only */}
        <div className="flex items-center justify-between mb-1">
          <div style={{ width: '70px' }} /> {/* Spacer for balance */}
          {!isUnavailable && (
            <span 
              className="px-2 py-0.5 rounded-full text-[10px] font-extrabold"
              style={{ 
                background: theme.sourceBadge.bg,
                border: `1px solid ${theme.sourceBadge.border}`,
                color: theme.sourceBadge.color,
              }}
            >
              {sourceLabel}
            </span>
          )}
        </div>
        
        {/* Centered Bureau Logo — same CDN assets as portal Dashboard */}
        <div className="flex justify-center mb-2">
          <BureauLogo bureau={bureau} isDark={theme.isDark} height="2rem" maxWidth="140px" />
        </div>
        
        {isUnavailable ? (
          <div className="text-center py-8">
            <AlertCircle size={36} className="mx-auto mb-3" style={{ color: theme.text.muted }} />
            <p className="text-sm font-bold" style={{ color: theme.text.primary }}>{t('creditReport.reportNotFound')}</p>
            <p className="text-xs mt-1" style={{ color: theme.text.muted }}>
              {t('creditReport.bureauDataUnavailable', { bureau })}
            </p>
          </div>
        ) : (
          /* Bureau body - compact gauge section */
          <div className="flex flex-col items-center">
            {/* Gauge container - 10-15% larger, tighter margins */}
            <div className="relative" style={{ width: '220px', height: '125px', marginTop: '-6px', marginBottom: '-4px' }}>
              <ScoreGauge 
                score={score || SCORE_MIN} 
                previousScore={SCORE_MIN}
                size={220}
                height={125}
                animate={true} 
                theme={{ isDark: theme.isDark, baseStroke: theme.gauge.baseStroke }}
                onScoreUpdate={handleScoreUpdate}
              />
              
              {/* Score display - positioned inside gauge */}
              <div 
                className="absolute left-1/2 font-black tracking-tighter text-center"
                style={{ 
                  top: '52px', 
                  transform: 'translateX(-50%)',
                  color: theme.text.primary,
                  fontSize: '34px',
                  lineHeight: 1,
                  letterSpacing: '-0.04em',
                }}
              >
                {displayScore > 0 ? displayScore : '--'}
              </div>
              
              {/* Status pill - tight to score */}
              <div 
                className="absolute left-1/2 px-3 py-0.5 rounded-full text-xs font-extrabold whitespace-nowrap"
                style={{ 
                  top: '88px',
                  transform: 'translateX(-50%)',
                  background: pill.style.bg,
                  color: pill.style.color,
                  lineHeight: 1.2,
                }}
              >
                {pill.label}
              </div>
              
              {/* Range labels */}
              <div 
                className="absolute text-[11px] font-bold"
                style={{ 
                  top: '105px', 
                  left: '28px',
                  color: theme.text.muted,
                }}
              >
                300
              </div>
              <div 
                className="absolute text-[11px] font-bold"
                style={{ 
                  top: '105px', 
                  right: '28px',
                  color: theme.text.muted,
                }}
              >
                850
              </div>
            </div>
            
            {/* Stats row - compact spacing */}
            <div className="grid grid-cols-3 gap-x-3 gap-y-0.5 w-full text-center">
              <StatLineMobile label={t('creditReport.statTotal')} value={data?.total_accounts} theme={theme} />
              <StatLineMobile label={t('creditReport.statOpen')} value={data?.open_accounts} theme={theme} />
              <StatLineMobile label={t('creditReport.statClosed')} value={data?.closed_accounts} theme={theme} />
              <StatLineMobile label={t('creditReport.statCollections')} value={data?.collections_count} isBad theme={theme} />
              <StatLineMobile label={t('creditReport.statDerogatory')} value={strictDerogatoryCount} isBad theme={theme} />
              <StatLineMobile label={t('creditReport.statInquiries')} value={data?.inquiries_count} theme={theme} />
            </div>

            <div
              className="mt-2 pt-2 border-t w-full"
              style={{ borderColor: theme.card.border }}
            >
              <ReportDateLines
                report={report}
                bureauName={bureau}
                t={t}
                theme={theme}
                testId={`bureau-dates-${bureau.toLowerCase()}`}
              />
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

// Stats line component
const StatLine = ({ label, value, isBad, theme }) => (
  <div 
    className="flex justify-between gap-4 text-sm"
    style={{ color: theme.text.muted }}
  >
    <span>{label}</span>
    <strong 
      className="font-bold"
      style={{ color: isBad && value > 0 ? theme.text.danger : theme.text.primary }}
    >
      {value ?? '--'}
    </strong>
  </div>
);

// Compact stats for mobile - stacked vertically
const StatLineMobile = ({ label, value, isBad, theme }) => (
  <div className="flex flex-col items-center">
    <strong 
      className="text-sm font-bold"
      style={{ color: isBad && value > 0 ? theme.text.danger : theme.text.primary }}
    >
      {value ?? '--'}
    </strong>
    <span className="text-[10px]" style={{ color: theme.text.muted }}>{label}</span>
  </div>
);

// Report Section Tab Navigation - Flow bar matching reference HTML
const SectionTabs = ({ activeTab, onTabChange, categoryCounts = {}, theme }) => {
  const { t } = useI18n();
  const tabs = [
    { id: 'potentially_negative', label: t('creditReport.tabPotentiallyNegative'), icon: AlertTriangle, category: 'accounts' },
    { id: 'accounts', label: t('creditReport.accounts'), icon: CreditCard, category: 'accounts' },
    { id: 'inquiries', label: t('creditReport.inquiries'), icon: Search, category: 'inquiries' },
    { id: 'personal_info', label: t('creditReport.personalInfo'), icon: User, category: 'personal_info' },
    { id: 'creditor_contacts', label: t('creditReport.creditorContacts'), icon: Phone, category: 'creditor_contacts' },
    { id: 'public_records', label: t('creditReport.publicRecords'), icon: FileSearch, category: 'public_records' },
  ];

  const isLastTab = (index) => index === tabs.length - 1;

  return (
    <nav 
      className="flex items-center gap-3 p-2 overflow-x-auto whitespace-nowrap"
      style={{ 
        background: theme.tabBar.bg,
        border: `1px solid ${theme.tabBar.border}`,
        borderRadius: '22px',
        boxShadow: theme.tabBar.shadow,
      }}
      data-testid="section-tabs"
    >
      {tabs.map((tab, index) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        const selectedCount = categoryCounts[tab.category] || 0;
        
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className="flex items-center gap-2.5 px-4 py-3.5 text-sm font-bold flex-shrink-0 cursor-pointer"
            style={{
              borderRadius: '16px',
              background: isActive ? theme.tabBar.activeBg : 'transparent',
              color: isActive ? theme.tabBar.activeText : theme.tabBar.inactiveText,
              opacity: isActive ? 1 : 0.96,
              transition: 'all 0.22s ease',
            }}
            data-testid={`tab-${tab.id}`}
          >
            <Icon size={16} />
            <span>{tab.label}</span>
            {selectedCount > 0 && (
              <span 
                className="px-1.5 py-0.5 text-xs font-bold rounded-full"
                style={{
                  background: isActive ? 'rgba(15,172,108,0.15)' : 'rgba(255,255,255,0.15)',
                  color: 'inherit',
                }}
              >
                {selectedCount}
              </span>
            )}
            {/* Trailing chevron */}
            {!isLastTab(index) && (
              <span 
                className="font-black ml-0.5"
                style={{ opacity: theme.tabBar.chevronOpacity }}
              >
                ›
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
};

// AI Badge Component - Themed
const AIBadge = ({ tag, theme }) => {
  // Use theme-aware tag styling
  const getTagStyle = () => {
    const t = tag?.toUpperCase() || '';
    
    // Red tags - negative/disputable
    if (t.includes('DISPUTABLE') || t.includes('HIGH IMPACT')) {
      return { bg: theme?.tag?.red?.bg, color: theme?.tag?.red?.color };
    }
    
    // Gold tags - warning/review
    if (t.includes('CAUTION') || t.includes('REVIEW') || t.includes('UTILIZATION') || t.includes('VERIFY')) {
      return { bg: theme?.tag?.gold?.bg, color: theme?.tag?.gold?.color };
    }
    
    // Green tags - positive/keep
    if (t.includes('CURRENT') || t.includes('KEEP OPEN') || t.includes('LOW PRIORITY')) {
      return { bg: theme?.tag?.green?.bg, color: theme?.tag?.green?.color };
    }
    
    // Default - neutral
    return { bg: theme?.statusPill?.neutral?.bg, color: theme?.statusPill?.neutral?.color };
  };

  const style = getTagStyle();

  return (
    <span 
      className="text-xs px-2 py-0.5 rounded-full font-semibold"
      style={{ background: style.bg, color: style.color }}
    >
      {tag}
    </span>
  );
};

// Filter out parser noise from tradelines
// PHASE 11.2: Enhanced minimum-valid-tradeline validation
const isValidTradeline = (tradeline) => {
  // Use canonical field name with fallback to legacy
  const name = (tradeline?.account_name || tradeline?.creditor_name)?.trim() || '';
  
  // Filter out empty names
  if (!name || name.length < 2) return false;
  
  // Filter out obvious parser noise patterns
  const noisePatterns = [
    /^OK\s+OK/i,           // "OK OK OK..."
    /^-+$/,                 // Just dashes
    /^\d+$/,                // Just numbers
    /^[A-Z]{1,2}$/,         // Single/double letters
    /CREDIT\s*REPORT/i,     // Section headers
    /IDENTITYIQ/i,          // Provider name
    /TRANSUNION|EXPERIAN|EQUIFAX/i, // Bureau names as account names
    /^SUMMARY$/i,           // Section labels
    /^ACCOUNT\s*(HISTORY|INFORMATION)?$/i,
    /^INQUIRIES$/i,
    /^PUBLIC\s*RECORDS?$/i,
    /^PERSONAL\s*INFORMATION?$/i,
    /BACK\s*TO\s*TOP/i,
    /^\*+$/,                // Just asterisks (masked account only)
  ];
  
  for (const pattern of noisePatterns) {
    if (pattern.test(name)) return false;
  }
  
  // Must have at least some alphabetic characters
  if (!/[a-zA-Z]{2,}/.test(name)) return false;
  
  // ============================================
  // MINIMUM VALID TRADELINE THRESHOLD (Phase 11.2)
  // ============================================
  // A tradeline must have creditor name PLUS at least one meaningful field
  // to be considered valid for UI display.
  
  const hasStatus = tradeline.status && 
    tradeline.status !== 'Unknown' && 
    tradeline.status !== 'unknown' &&
    tradeline.status !== '--';
  
  const hasBalance = tradeline.balance !== null && 
    tradeline.balance !== undefined &&
    tradeline.balance !== '--';
  
  const hasDateOpened = tradeline.date_opened && 
    tradeline.date_opened !== '--' &&
    tradeline.date_opened !== 'N/A';
  
  const hasDateReported = (tradeline.date_reported || tradeline.last_reported_date) && 
    tradeline.date_reported !== '--' &&
    tradeline.last_reported_date !== '--';
  
  const hasAccountType = tradeline.account_type && 
    tradeline.account_type !== 'unknown' &&
    tradeline.account_type !== '--';
  
  const hasPaymentStatus = tradeline.payment_status && 
    tradeline.payment_status !== 'Unknown' &&
    tradeline.payment_status !== '--';
  
  const hasCreditLimit = tradeline.credit_limit !== null && 
    tradeline.credit_limit !== undefined &&
    tradeline.credit_limit !== '--';
  
  const hasBureau = tradeline.bureau && 
    tradeline.bureau !== 'Unknown' &&
    tradeline.bureau !== '--';
  
  // Count valid fields (need at least 2 beyond just the name)
  const validFieldCount = [
    hasStatus, hasBalance, hasDateOpened, hasDateReported,
    hasAccountType, hasPaymentStatus, hasCreditLimit, hasBureau
  ].filter(Boolean).length;
  
  // ============================================
  // CRITICAL: NEGATIVE ITEMS MUST NOT BE FILTERED OUT
  // ============================================
  // Negative items (collections, charge-offs, etc.) are the most important
  // for the dispute workflow. They MUST appear even if data is sparse.
  const isNegativeItem = isNegativeAccount(tradeline);
  
  // Negative items are ALWAYS valid for UI, even with sparse data
  if (isNegativeItem) {
    return true;
  }
  
  // Minimum threshold: Must have at least 2 meaningful fields for non-negative items
  if (validFieldCount < 2) {
    // Mark as incomplete for debug visibility
    tradeline._incomplete = true;
    tradeline._validFieldCount = validFieldCount;
    return false;
  }
  
  return true;
};

// Check if tradeline has meaningful comparison data
const hasValidComparisonData = (tradeline) => {
  // Must have at least one bureau with real data
  const bureaus = tradeline.bureaus_reported || tradeline.bureaus || [];
  if (!bureaus || bureaus.length === 0) return false;
  
  // Check if we have any non-null values for comparison fields
  const comparisonFields = ['balance', 'credit_limit', 'status', 'payment_status', 'date_opened'];
  
  let hasData = false;
  for (const field of comparisonFields) {
    const value = tradeline[field];
    if (value !== null && value !== undefined && value !== '--' && value !== 'Unknown') {
      hasData = true;
      break;
    }
  }
  
  return hasData;
};

// Compute tradeline completeness score
const computeCompletenessScore = (tradeline) => {
  const fields = [
    { name: 'creditor_name', value: tradeline.creditor_name || tradeline.account_name },
    { name: 'account_number', value: tradeline.account_number },
    { name: 'account_type', value: tradeline.account_type },
    { name: 'status', value: tradeline.status },
    { name: 'payment_status', value: tradeline.payment_status },
    { name: 'balance', value: tradeline.balance },
    { name: 'credit_limit', value: tradeline.credit_limit },
    { name: 'date_opened', value: tradeline.date_opened },
    { name: 'date_reported', value: tradeline.date_reported || tradeline.last_reported_date },
    { name: 'bureau', value: tradeline.bureau },
    { name: 'bureaus', value: tradeline.bureaus_reported?.length > 0 ? 'has' : null },
  ];
  
  const validFields = fields.filter(f => {
    const v = f.value;
    return v !== null && v !== undefined && v !== '' && v !== '--' && v !== 'Unknown' && v !== 'unknown';
  });
  
  return {
    score: validFields.length / fields.length,
    validCount: validFields.length,
    totalCount: fields.length,
    validFields: validFields.map(f => f.name),
    hasMinimumForUI: validFields.length >= 4,
    hasMinimumForComparison: validFields.length >= 3 && tradeline.bureaus_reported?.length > 0,
  };
};

const CREDIT_REPORT_BUREAUS = ['transunion', 'experian', 'equifax'];

const getBureauScoreValue = (bureauData) => {
  if (!bureauData) return null;
  const rawScore = bureauData.score;
  if (rawScore == null) return null;
  if (typeof rawScore === 'object') return rawScore.value ?? null;
  const numericScore = Number(rawScore);
  return Number.isFinite(numericScore) ? numericScore : null;
};

const isPlaceholderCreditScore = (score) => score == null || score === 0 || score === 300;

const bureauHasRealParsedScore = (bureauData) => {
  if (!bureauData) return false;
  const score = getBureauScoreValue(bureauData);
  const scoreSource = bureauData.score_source;
  if (scoreSource === 'no_data' || scoreSource === 'fallback') return false;
  if (isPlaceholderCreditScore(score) || score < 301 || score > 850) return false;
  return scoreSource === 'explicit' || scoreSource == null;
};

/**
 * True only when the report has real parsed bureau scores (not placeholder 300 shells).
 * parser_success / report_saved false explicitly block display; when omitted, infer from data.
 */
export const hasValidParsedCreditReport = (report) => {
  if (!report) return false;

  if (report.parser_success === false || report.report_saved === false) return false;
  if (report.parsing_metadata?.is_demo_data) return false;

  const bureauScores = CREDIT_REPORT_BUREAUS.map((bureau) => getBureauScoreValue(report[bureau]));
  if (bureauScores.length > 0 && bureauScores.every((score) => score === 300)) return false;

  const explicitBureauCount = CREDIT_REPORT_BUREAUS.filter((bureau) =>
    bureauHasRealParsedScore(report[bureau])
  ).length;
  if (explicitBureauCount === 0) return false;

  const validTradelineCount = (report.tradelines || []).filter(isValidTradeline).length;
  if (validTradelineCount > 0) return true;

  return explicitBureauCount > 0;
};

// Tradeline Row Component - Enhanced with bureau-level selection, caution states, and dispute tray integration
// Uses canonical field names with fallbacks for backward compatibility
const TradelineRow = ({ tradeline, onViewDetails, onAddDispute, onDisputeWithCaution, onToggleBureau, selectedBureaus, onToggleTray, isTraySelected, theme, rowIndex = 0 }) => {
  const { t } = useI18n();
  // Helper to get field value with canonical name fallback
  const getField = (canonical, legacy) => tradeline[canonical] ?? tradeline[legacy];
  
  // Determine dispute eligibility
  const disputeEligibility = getDisputeEligibility(tradeline);
  const accountSeverity = getAccountSeverity(tradeline);
  const needsCaution = accountSeverity === 'bureau_difference' || disputeEligibility.status === 'CAUTION';

  // Icon mapping: red=negative only, amber=bureau difference, green=current, gray=unknown
  const getIconStyle = () => {
    if (accountSeverity === 'negative') {
      return { bg: theme?.tag?.red?.bg || 'rgba(239,68,68,0.12)', color: theme?.icon?.danger || '#EF4444' };
    }
    if (accountSeverity === 'bureau_difference') {
      return { bg: theme?.tag?.gold?.bg || 'rgba(234,178,58,0.16)', color: theme?.icon?.warn || '#EAB23A' };
    }
    if (accountSeverity === 'unknown') {
      return { bg: theme?.statusPill?.neutral?.bg || 'rgba(148,163,184,0.16)', color: theme?.text?.muted || '#94A3B8' };
    }
    return { bg: theme?.tag?.green?.bg || 'rgba(15,172,108,0.12)', color: theme?.icon?.primary || '#0D9F64' };
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '' || amount === '--') return '--';
    if (typeof amount === 'string') {
      const numStr = amount.replace(/[$,]/g, '');
      const num = parseFloat(numStr);
      if (isNaN(num)) return amount;
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return dateStr;
  };

  // Get values using canonical names with legacy fallbacks
  const accountName = getField('account_name', 'creditor_name') || 'Unknown';
  const accountNumber = getField('account_number_masked', 'masked_account_number') || '****';
  const accountStatus = getField('account_status', 'status') || 'Unknown';
  const creditLimit = getField('credit_limit', 'limit');
  
  // Bureau cell — ✓ for reporting bureaus, -- otherwise
  const BureauCell = ({ bureau }) => {
    const bureauLower = bureau.toLowerCase();
    const isReported = tradelineReportsBureau(tradeline, bureau);
    const isSelected = selectedBureaus?.[bureauLower] || false;
    
    if (!isReported) {
      return (
        <div className="flex items-center justify-center">
          <span className="text-xs" style={{ color: theme?.text?.muted }} data-testid={`bureau-missing-${tradeline.tradeline_id}-${bureauLower}`}>--</span>
        </div>
      );
    }
    
    return (
      <div className="flex items-center justify-center">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleBureau?.(tradeline.tradeline_id, bureauLower);
          }}
          className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
          style={{
            background: isSelected ? theme?.checkBox?.bg : 'transparent',
            border: `2px solid ${isSelected ? theme?.checkBox?.border : theme?.border?.b2}`,
          }}
          data-testid={`bureau-select-${tradeline.tradeline_id}-${bureauLower}`}
          title={`Reported to ${bureau}`}
        >
          {isSelected ? (
            <CheckCircle size={14} style={{ color: theme?.checkBox?.color }} />
          ) : (
            <Check size={12} style={{ color: theme?.icon?.primary }} data-testid={`bureau-check-${tradeline.tradeline_id}-${bureauLower}`} />
          )}
        </button>
      </div>
    );
  };

  // Handle dispute button click - check if caution is needed
  const handleDisputeClick = () => {
    if (needsCaution) {
      onDisputeWithCaution?.(tradeline);
    } else {
      onAddDispute(tradeline);
    }
  };

  // Evidence-based tags only — never invent REVIEW without reasons.
  const getDisplayTag = () => getPrimaryDisplayTag(tradeline);

  const displayTag = getDisplayTag();
  const iconStyle = getIconStyle();
  
  // Row styling with alternating backgrounds
  const rowBg = isTraySelected 
    ? theme?.table?.rowHover 
    : rowIndex % 2 === 0 
      ? theme?.table?.row 
      : theme?.table?.rowAlt;

  return (
    <tr 
      className="transition-colors cursor-pointer hover:opacity-90"
      style={{ 
        background: rowBg,
        borderBottom: `1px solid ${theme?.table?.border}`,
      }}
      data-testid={`tradeline-${tradeline.tradeline_id}`}
      onClick={(e) => {
        // Click on row opens detail view (Bureau Comparison)
        // Exclude clicks on action buttons, checkboxes, and bureau toggles
        const target = e.target;
        const isButton = target.closest('button');
        const isCheckbox = target.closest('[role="checkbox"]');
        const isLink = target.closest('a');
        
        // If the click is on an action button/checkbox/link, don't open details
        if (isButton || isCheckbox || isLink) {
          return;
        }
        
        // Open the detail view (Bureau Comparison table)
        onViewDetails?.(tradeline);
      }}
    >
      {/* Account Selection Column - For Dispute Tray */}
      <td className="py-4 px-3" onClick={(e) => e.stopPropagation()}>
        <Checkbox 
          checked={isTraySelected}
          onCheckedChange={() => onToggleTray?.(tradeline)}
          className="data-[state=checked]:bg-emerald-600"
          data-testid={`tray-select-${tradeline.tradeline_id}`}
        />
      </td>
      
      {/* Account Info */}
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: iconStyle.bg }}
          >
            {accountSeverity === 'negative' ? (
              <AlertTriangle size={18} style={{ color: iconStyle.color }} />
            ) : accountSeverity === 'bureau_difference' || needsCaution ? (
              <ShieldAlert size={18} style={{ color: iconStyle.color }} />
            ) : (
              <CreditCard size={18} style={{ color: iconStyle.color }} />
            )}
          </div>
          <div>
            <p className="font-semibold" style={{ color: theme?.text?.primary }}>{accountName}</p>
            <p className="text-xs" style={{ color: theme?.text?.muted }}>
              {accountNumber} 
              {tradeline.account_type && tradeline.account_type !== 'unknown' && ` • ${tradeline.account_type}`}
            </p>
          </div>
        </div>
      </td>
      <td className="py-4 px-4">
        <div>
          <span 
            className="text-xs px-2 py-1 rounded-full font-semibold"
            style={{ 
              background: accountSeverity === 'negative' || (accountStatus || '').toLowerCase().includes('derogatory') 
                ? theme?.tag?.red?.bg 
                : (accountStatus || '').toLowerCase() === 'open' 
                  ? theme?.tag?.green?.bg 
                  : theme?.statusPill?.neutral?.bg,
              color: accountSeverity === 'negative' || (accountStatus || '').toLowerCase().includes('derogatory')
                ? theme?.tag?.red?.color
                : (accountStatus || '').toLowerCase() === 'open'
                  ? theme?.tag?.green?.color
                  : theme?.statusPill?.neutral?.color,
            }}
          >
            {accountStatus}
          </span>
          {tradeline.payment_status && (
            <p 
              className="text-xs mt-1"
              style={{ 
                color: (tradeline.payment_status || '').toLowerCase().includes('late') || 
                       (tradeline.payment_status || '').toLowerCase().includes('collection')
                  ? theme?.text?.danger 
                  : theme?.text?.secondary 
              }}
            >
              {tradeline.payment_status}
            </p>
          )}
        </div>
      </td>
      <td className="py-4 px-4">
        <p className="text-sm font-semibold" style={{ color: theme?.text?.primary }}>{formatCurrency(tradeline.balance)}</p>
        {creditLimit > 0 && (
          <p className="text-xs" style={{ color: theme?.text?.muted }}>Limit: {formatCurrency(creditLimit)}</p>
        )}
      </td>
      <td className="py-4 px-4">
        <div className="text-xs" style={{ color: theme?.text?.secondary }}>
          <p>Opened: {formatDate(tradeline.date_opened)}</p>
          <p style={{ color: theme?.text?.muted }}>Reported: {formatDate(getField('last_reported', 'date_reported'))}</p>
        </div>
      </td>
      
      {/* Bureau Selection Columns */}
      <td className="py-4 px-2">
        <BureauCell bureau="TransUnion" />
      </td>
      <td className="py-4 px-2">
        <BureauCell bureau="Experian" />
      </td>
      <td className="py-4 px-2">
        <BureauCell bureau="Equifax" />
      </td>
      
      <td className="py-4 px-4">
        <div className="flex flex-wrap gap-1">
          {displayTag && <AIBadge tag={displayTag} theme={theme} />}
        </div>
      </td>
      <td className="py-4 px-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewDetails(tradeline)}
            data-testid={`view-${tradeline.tradeline_id}`}
            title="View Details"
            style={{ color: theme?.icon?.neutral }}
          >
            <Eye size={16} />
            <span className="ml-1">View</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDisputeClick}
            data-testid={`dispute-${tradeline.tradeline_id}`}
            style={{ color: needsCaution ? theme?.icon?.warn : theme?.icon?.primary }}
          >
            {needsCaution ? <ShieldAlert size={16} /> : <Plus size={16} />}
            <span className="ml-1">{t('creditReport.dispute')}</span>
          </Button>
        </div>
      </td>
    </tr>
  );
};

// Tradeline Detail Modal - Premium theme-aware popup styling
// Contains Bureau Comparison, Payment History, Creditor Contact, AI Analysis
// Supports both light and dark modes matching HTML reference
const TradelineDetailModal = ({ tradeline, creditorContacts, open, onClose, buildBureauAccount, darkMode = false }) => {
  if (!tradeline) return null;

  // Get account name for display
  const accountName = tradeline.account_name || tradeline.creditor_name || 'Unknown';
  
  // Check if this is a multi-bureau account (has bureau_data with differences)
  const hasBureauData = tradeline?.bureau_data && Object.keys(tradeline.bureau_data).length > 0;
  // Use only actual reported bureaus — never invent all three
  const bureausReported = getTradelineReportedBureaus(tradeline);
  const isMultiBureau = bureausReported.length > 1;

  // Find matching creditor contact
  const findCreditorContact = () => {
    if (!creditorContacts || creditorContacts.length === 0) return null;
    const creditorName = accountName.toLowerCase();
    return creditorContacts.find(cc => {
      const contactName = cc.name?.toLowerCase() || cc.raw_name?.toLowerCase() || '';
      return contactName.includes(creditorName) || 
             creditorName.includes(contactName.split(' ')[0]) ||
             creditorName.replace(/\s+/g, '').includes(contactName.replace(/\s+/g, '').slice(0, 8));
    });
  };

  const contact = findCreditorContact();
  
  // Determine if account is negative
  const isNegative = isNegativeAccount(tradeline);

  // Theme-aware popup tokens matching HTML reference
  const popupTheme = darkMode ? {
    // Dark mode - deep emerald glass
    shell: {
      bg: 'rgba(7,28,21,.82)',
      border: 'rgba(142,218,182,.12)',
      shadow: '0 24px 80px rgba(0,0,0,.42)',
    },
    surface: {
      s1: 'rgba(10,34,25,.74)',
      s2: 'rgba(12,38,28,.64)',
    },
    text: {
      primary: '#EAF7F0',
      secondary: '#C7DDD0',
      muted: '#93AE9F',
    },
    accent: {
      green: '#0FAC6C',
      greenLight: '#8FE0B7',
      gold: '#EAB23A',
      goldLight: '#F0C160',
      red: '#EF4444',
      redLight: '#F27A7A',
      ai: '#EAB23A', // AI accent uses gold, not purple
    },
    badge: {
      negative: { bg: 'rgba(239,68,68,.12)', border: 'rgba(239,68,68,.14)', color: '#F27A7A' },
      positive: { bg: 'rgba(15,172,108,.14)', border: 'rgba(15,172,108,.18)', color: '#8FE0B7' },
    },
    innerGlow: 'linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,0))',
    analysisBox: {
      bg: 'rgba(12,38,28,.64)',
      border: 'rgba(142,218,182,.10)',
    },
  } : {
    // Light mode - premium white glass
    shell: {
      bg: 'rgba(255,255,255,.84)',
      border: 'rgba(15,172,108,.10)',
      shadow: '0 20px 60px rgba(16,24,40,.12)',
    },
    surface: {
      s1: 'rgba(255,255,255,.76)',
      s2: 'rgba(255,255,255,.68)',
    },
    text: {
      primary: '#111827',
      secondary: '#4B5563',
      muted: '#6B7280',
    },
    accent: {
      green: '#0FAC6C',
      greenLight: '#0B8C58',
      gold: '#C98A1B',
      goldLight: '#B88A15',
      red: '#EF4444',
      redLight: '#E45757',
      ai: '#C98A1B', // AI accent uses gold, not purple
    },
    badge: {
      negative: { bg: 'rgba(239,68,68,.10)', border: 'rgba(239,68,68,.14)', color: '#E45757' },
      positive: { bg: 'rgba(15,172,108,.10)', border: 'rgba(15,172,108,.16)', color: '#0B8C58' },
    },
    innerGlow: 'linear-gradient(180deg, rgba(255,255,255,.08), rgba(255,255,255,0))',
    analysisBox: {
      bg: 'rgba(255,255,255,.68)',
      border: 'rgba(15,172,108,.08)',
    },
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent 
        className="!flex !flex-col gap-0 p-0 w-[calc(100vw-1.25rem)] max-w-[calc(100vw-1.25rem)] sm:w-full sm:max-w-[980px] h-[min(calc(100dvh-48px),900px)] max-h-[min(calc(100dvh-48px),900px)] overflow-hidden top-[max(0.5rem,env(safe-area-inset-top))] translate-y-0 sm:top-[50%] sm:translate-y-[-50%] rounded-2xl sm:rounded-[32px]"
        style={{
          background: popupTheme.shell.bg,
          border: `1px solid ${popupTheme.shell.border}`,
          boxShadow: popupTheme.shell.shadow,
          backdropFilter: 'blur(18px) saturate(140%)',
        }}
        data-testid="tradeline-detail-modal"
      >
        {/* Inner glow overlay */}
        <div 
          style={{ 
            position: 'absolute', 
            inset: 0, 
            pointerEvents: 'none',
            background: 'linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,0))',
            borderRadius: '32px',
          }} 
        />
        
        {/* Modal Header — reserve top-right for Dialog close; stack badge on mobile */}
        <div 
          className="flex-shrink-0 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 pr-14 sm:pr-16 relative z-10"
          data-testid="modal-header"
        >
          <div className="flex items-start gap-3 min-w-0">
            <div 
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-[14px] flex items-center justify-center flex-shrink-0"
              style={{ 
                background: isNegative ? 'rgba(239,68,68,.12)' : 'rgba(15,172,108,.12)',
                border: `1px solid ${isNegative ? 'rgba(239,68,68,.16)' : 'rgba(15,172,108,.16)'}`,
              }}
            >
              {isNegative ? (
                <AlertTriangle size={20} style={{ color: popupTheme.accent.red }} />
              ) : (
                <CreditCard size={20} style={{ color: popupTheme.accent.green }} />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h1 
                className="text-base sm:text-lg font-black tracking-tight m-0 break-words leading-snug pr-1"
                style={{ color: popupTheme.text.primary, letterSpacing: '-0.03em' }}
              >
                {accountName}
              </h1>
              <p 
                className="text-xs sm:text-sm m-0 mt-0.5 truncate"
                style={{ color: popupTheme.text.muted }}
              >
                {tradeline.account_number_masked || tradeline.account_number || '****'}
              </p>
              {isNegative && (
                <span 
                  className="inline-flex items-center mt-2 px-2.5 py-1 rounded-full text-xs font-extrabold whitespace-nowrap"
                  style={{ 
                    background: popupTheme.badge.negative.bg,
                    border: `1px solid ${popupTheme.badge.negative.border}`,
                    color: popupTheme.badge.negative.color,
                  }}
                >
                  Negative
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-4 sm:space-y-5 px-4 sm:px-6 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-6 relative z-10">
          {/* Bureau Comparison Section - Primary view */}
          <BureauComparisonSection
            tradeline={tradeline}
            accountName={accountName}
            darkMode={darkMode}
          />

          {/* Payment History Grid */}
          <PaymentHistoryGrid 
            account={tradeline} 
            defaultMonths={12} 
            darkMode={darkMode}
          />

          {/* Creditor Contact Info */}
          {contact && (
            <div 
              className="rounded-[18px] overflow-hidden"
              style={{ 
                background: popupTheme.surface.s1,
                border: `1px solid ${popupTheme.shell.border}`,
                backdropFilter: 'blur(14px)',
              }}
            >
              {/* Section Header */}
              <div 
                className="flex items-center gap-2.5 px-[18px] pt-[18px] pb-3"
                style={{ borderBottom: `1px solid ${darkMode ? 'rgba(142,218,182,.08)' : 'rgba(15,172,108,.08)'}` }}
              >
                <Phone size={16} style={{ color: popupTheme.accent.greenLight }} />
                <span 
                  className="font-black text-[15px]"
                  style={{ color: popupTheme.text.primary }}
                >
                  Creditor Contact Information
                </span>
              </div>
              {/* Content Wrapper */}
              <div 
                className="px-[18px] py-4"
                style={{ 
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                }}
              >
                <div 
                  className="text-sm leading-relaxed"
                  style={{ color: popupTheme.text.secondary }}
                >
                  <p 
                    className="font-medium" 
                    style={{ 
                      color: popupTheme.text.primary,
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {contact.name || contact.raw_name}
                  </p>
                  {(contact.full_address || contact.city) && (
                    <p 
                      className="mt-2"
                      style={{ 
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                      }}
                    >
                      {contact.full_address || `${contact.city}, ${contact.state} ${contact.zip || ''}`}
                    </p>
                  )}
                  {contact.phone && (
                    <a 
                      href={`tel:${contact.phone}`} 
                      className="block mt-1 hover:underline"
                      style={{ color: popupTheme.accent.greenLight }}
                    >
                      {contact.phone}
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* AI Analysis */}
          {(tradeline.ai_risk_tag || tradeline.ai_action_tag || tradeline.is_negative) && (
            <div 
              className="rounded-[18px] overflow-hidden"
              style={{ 
                background: popupTheme.surface.s1,
                border: `1px solid ${popupTheme.shell.border}`,
                backdropFilter: 'blur(14px)',
              }}
            >
              {/* Section Header */}
              <div 
                className="flex items-center gap-2.5 px-[18px] pt-[18px] pb-3"
                style={{ borderBottom: `1px solid ${darkMode ? 'rgba(142,218,182,.08)' : 'rgba(15,172,108,.08)'}` }}
              >
                <Sparkles size={16} style={{ color: popupTheme.accent.ai }} />
                <span 
                  className="font-black text-[15px]"
                  style={{ color: popupTheme.text.primary }}
                >
                  AI Analysis
                </span>
              </div>
              {/* Content Wrapper */}
              <div 
                className="px-[18px] py-4"
                style={{ 
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                }}
              >
                <div 
                  className="p-4 rounded-2xl"
                  style={{ 
                    background: popupTheme.analysisBox.bg,
                    border: `1px solid ${popupTheme.analysisBox.border}`,
                  }}
                >
                  <p 
                    className="text-sm font-extrabold mb-2"
                    style={{ color: popupTheme.text.primary }}
                  >
                    Suggested opportunity
                  </p>
                  <p 
                    className="text-sm leading-relaxed"
                    style={{ 
                      color: popupTheme.text.secondary,
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                    }}
                  >
                    {tradeline.ai_risk_tag && `Risk: ${tradeline.ai_risk_tag}. `}
                    {tradeline.ai_action_tag && `Recommended: ${tradeline.ai_action_tag}. `}
                    {tradeline.dispute_eligibility && `Dispute eligibility: ${tradeline.dispute_eligibility}. `}
                    {isNegative && 'Bureau differences may support a targeted factual or Metro 2 style review.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Debug Panel Component
const DebugPanel = ({ report, isOpen, onToggle }) => {
  if (!report) return null;

  return (
    <div className="bg-stone-900 rounded-xl overflow-hidden" data-testid="debug-panel">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between text-white hover:bg-stone-800 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-amber-400" />
          <span className="font-medium">Developer Debug Panel</span>
        </div>
        <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="p-4 border-t border-slate-700 space-y-4">
          {/* Bureau Score Verification */}
          <div>
            <h4 className="text-sm font-medium text-amber-400 mb-2">Bureau Scores (Parsed vs Rendered)</h4>
            <div className="grid grid-cols-3 gap-3">
              {['transunion', 'experian', 'equifax'].map(bureau => {
                const data = report[bureau];
                return (
                  <div key={bureau} className="bg-stone-800 p-3 rounded-lg">
                    <p className="text-xs text-slate-400 capitalize">{bureau}</p>
                    <p className="text-lg font-bold text-white">{data?.score ?? '--'}</p>
                    <p className="text-xs text-emerald-400">
                      Source: {data?.score_source || 'unknown'}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Summary Values */}
          <div>
            <h4 className="text-sm font-medium text-amber-400 mb-2">Summary Values by Bureau</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-400">
                    <th className="text-left py-1 px-2">Field</th>
                    <th className="text-center py-1 px-2">TransUnion</th>
                    <th className="text-center py-1 px-2">Experian</th>
                    <th className="text-center py-1 px-2">Equifax</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  {['total_accounts', 'open_accounts', 'closed_accounts', 'derogatory_count', 'collections_count', 'inquiries_count'].map(field => (
                    <tr key={field} className="border-t border-slate-700">
                      <td className="py-1 px-2 text-slate-400">{field.replace('_', ' ')}</td>
                      <td className="py-1 px-2 text-center">{report.transunion?.[field] ?? '--'}</td>
                      <td className="py-1 px-2 text-center">{report.experian?.[field] ?? '--'}</td>
                      <td className="py-1 px-2 text-center">{report.equifax?.[field] ?? '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tradelines Debug */}
          <div>
            <h4 className="text-sm font-medium text-amber-400 mb-2">Tradelines Parsed</h4>
            <div className="text-xs text-white">
              <p>Total in response: {report.tradelines?.length ?? 0}</p>
              <p>Valid (after filter): {report.tradelines?.filter(isValidTradeline).length ?? 0}</p>
              <p>Filtered out: {(report.tradelines?.length ?? 0) - (report.tradelines?.filter(isValidTradeline).length ?? 0)}</p>
            </div>
          </div>

          {/* Provider Info */}
          <div className="text-xs text-slate-400">
            <p>Source: {getReportSourceDisplay(report).label}</p>
            <p>Report ID: {report.report_id}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Shown when import saved provider/refresh status but no parsed report data yet
const ImportIncompleteStatusCard = ({ theme, onRetryImport, onUploadReport }) => {
  const { t } = useI18n();
  return (
  <section
    className="relative overflow-hidden p-4 sm:p-5"
    style={{
      background: theme.card.bg,
      border: `1px solid ${theme.card.border}`,
      borderRadius: '18px',
      boxShadow: theme.card.shadow,
      backdropFilter: 'blur(16px) saturate(140%)',
    }}
    data-testid="import-incomplete-status-card"
  >
    <div style={{ position: 'absolute', inset: 0, background: theme.card.innerGlow, pointerEvents: 'none' }} />
    <div className="relative z-10 flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: theme.shield.bg }}
        >
          <Clock size={18} style={{ color: theme.shield.color }} />
        </div>
        <div>
          <h3 className="text-sm sm:text-base font-extrabold mb-1" style={{ color: theme.text.primary }}>
            {t('creditReport.refreshStatusSaved')}
          </h3>
          <p className="text-xs sm:text-sm leading-relaxed" style={{ color: theme.text.muted }}>
            {t('creditReport.refreshStatusBody')}
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          onClick={onRetryImport}
          className="h-10 sm:h-11 font-bold text-sm"
          style={{ background: theme.green[600], color: '#fff', borderRadius: '12px' }}
          data-testid="incomplete-retry-import-btn"
        >
          <RefreshCw size={14} className="mr-2" />
          {t('creditReport.retryImport')}
        </Button>
        <Button
          variant="outline"
          onClick={onUploadReport}
          className="h-10 sm:h-11 font-bold text-sm"
          style={{
            background: theme.surface.s2,
            border: `1px solid ${theme.border.b2}`,
            borderRadius: '12px',
            color: theme.green[700],
          }}
          data-testid="incomplete-upload-report-btn"
        >
          <Upload size={14} className="mr-2" />
          {t('creditReport.uploadReportFile')}
        </Button>
      </div>
    </div>
  </section>
  );
};

// Empty State Component - Premium themed with positive-box
const EmptyState = ({ icon: Icon, title, description, theme, isPositive = false }) => (
  <div className="text-center py-12 px-6">
    <div 
      className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
      style={{ 
        background: isPositive ? theme?.positiveBox?.bg : theme?.emptyIcon?.bg,
        border: `1px solid ${isPositive ? theme?.positiveBox?.border : theme?.emptyIcon?.border}`,
      }}
    >
      <Icon size={28} style={{ color: isPositive ? theme?.positiveBox?.color : theme?.emptyIcon?.color }} />
    </div>
    <h3 
      className="text-lg font-bold mb-2"
      style={{ color: theme?.text?.primary }}
    >
      {title}
    </h3>
    <p 
      className="text-sm max-w-md mx-auto"
      style={{ color: theme?.text?.muted }}
    >
      {description}
    </p>
    {isPositive && (
      <div 
        className="mt-4 p-4 rounded-xl max-w-lg mx-auto"
        style={{ 
          background: theme?.positiveBox?.bg,
          border: `1px solid ${theme?.positiveBox?.border}`,
        }}
      >
        <div className="flex items-start gap-3">
          <CheckCircle size={18} className="mt-0.5 flex-shrink-0" style={{ color: theme?.positiveBox?.color }} />
          <p 
            className="text-sm text-left"
            style={{ color: theme?.positiveBox?.color }}
          >
            This is generally a positive sign for your credit profile.
          </p>
        </div>
      </div>
    )}
  </div>
);

// Main Credit Report Component
const CreditReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getAuthHeaders, user } = useAuth();
  const { isInSupportMode, supportContext } = useSupportContext();
  
  // Use centralized report data context for SINGLE SOURCE OF TRUTH
  const { 
    reportData: contextReportData, 
    loading: contextLoading, 
    hasReport,
    refreshData,
    selectReport,
    selectedReportId,
    fetchAvailableReports,
    bureauScores,
    averageScore,
    getTradelines
  } = useReportData();
  
  const { t } = useI18n();
  const { resolvedTheme } = useTheme();
  const theme = getCreditReportTheme(resolvedTheme === 'dark');
  const mainFilters = useMemo(() => getMainFilters(t), [t]);
  
  // Use dispute tray for multi-item selection
  const { 
    toggleItem, 
    isItemSelected, 
    getCategoryCount,
    categoryCounts,
    addItem,
    removeItem,
    selectedItems,
    CATEGORIES 
  } = useDisputeTray();
  
  // Local state for UI interactions
  const [selectedBureau, setSelectedBureau] = useState('all');
  const [mainFilter, setMainFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(() => creditReportTabFromPathname(location.pathname));
  const [selectedTradeline, setSelectedTradeline] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedForDispute, setSelectedForDispute] = useState({}); // { tradeline_id: { transunion: true, experian: false, equifax: true } }
  // debugOpen state removed - debug panels not shown to users
  
  // State for positive account warning modal
  const [showCautionModal, setShowCautionModal] = useState(false);
  const [cautionTradeline, setCautionTradeline] = useState(null);
  
  // State for Request Report Modal (secondary entry point)
  const [showRequestReportModal, setShowRequestReportModal] = useState(false);
  
  // State for Re-import Modal
  const [showReimportModal, setShowReimportModal] = useState(false);
  const [reimportPreferAutoStart, setReimportPreferAutoStart] = useState(false);
  const [refreshImportId, setRefreshImportId] = useState(null);
  const refreshImportStartingRef = useRef(false);
  const refreshImportIdRef = useRef(null);

  useEffect(() => {
    refreshImportIdRef.current = refreshImportId;
  }, [refreshImportId]);

  useEffect(() => {
    const tabFromPath = creditReportTabFromPathname(location.pathname);
    setActiveTab((current) => (current === tabFromPath ? current : tabFromPath));
  }, [location.pathname]);

  const handleSectionTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    const nextPath = creditReportPathForTab(tabId);
    if (nextPath !== location.pathname) {
      navigate(nextPath, { replace: true });
    }
  }, [location.pathname, navigate]);
  
  // Handle toggling item in dispute tray
  const handleToggleTrayItem = (item, category) => {
    toggleItem(category, item);
  };

  // Helper function to build bureau-specific account from bureau_data
  const buildBureauAccount = (tradeline, bureau) => {
    if (!tradeline) return null;
    
    const bureauData = tradeline.bureau_data || {};
    const bureauAccount = { ...tradeline };
    
    // Override with bureau-specific values where available
    Object.keys(bureauData).forEach(field => {
      const fieldData = bureauData[field];
      if (fieldData && typeof fieldData === 'object' && fieldData[bureau] !== undefined) {
        bureauAccount[field] = fieldData[bureau];
      }
    });
    
    return bureauAccount;
  };


  // Map context data to expected format for backward compatibility
  const report = useMemo(() => {
    if (!contextReportData) return null;
    
    // Transform from centralized format to page's expected format
    return {
      report_id: contextReportData.report_id,
      transunion: contextReportData.transunion,
      experian: contextReportData.experian,
      equifax: contextReportData.equifax,
      tradelines: contextReportData.tradelines || [],
      inquiries: contextReportData.inquiries || [],
      public_records: contextReportData.public_records || [],
      personal_info: contextReportData.personal_info,
      addresses: contextReportData.addresses || [],
      creditor_contacts: contextReportData.creditor_contacts || [],
      import_source: contextReportData.import_source,
      import_provider: contextReportData.import_provider,
      created_at: contextReportData.created_at,
      updated_at: contextReportData.updated_at,
      imported_at: contextReportData.imported_at,
      last_imported_at: contextReportData.last_imported_at,
      refresh_completed_at: contextReportData.refresh_completed_at,
      report_date: contextReportData.report_date,
      reportDate: contextReportData.reportDate,
      parsed_data: contextReportData.parsed_data,
      metadata: contextReportData.metadata,
      import_metadata: contextReportData.import_metadata,
      source_report_locked: contextReportData.source_report_locked,
      // Include derived values with clear labeling
      derived: contextReportData.derived,
      workflow: contextReportData.workflow,
      parsing_metadata: contextReportData.parsing_metadata,
      parsing_debug: contextReportData.parsing_debug,
      parser_success: contextReportData.parser_success,
      report_saved: contextReportData.report_saved,
    };
  }, [contextReportData]);

  // Full /api/report-data response — use for credential card and date-aware child components.
  const activeReportData = contextReportData;

  const effectiveUserId =
    activeReportData?.user_id
    || (isInSupportMode ? supportContext?.effective_user_id : user?.user_id)
    || null;

  const handleImportDismissed = useCallback(() => {
    if (report?.import_provider && report?.report_id) {
      markAutoRefreshDismissed(report.import_provider, report.report_id);
    }
    refreshImportStartingRef.current = false;
    setRefreshImportId(null);
  }, [report?.import_provider, report?.report_id]);

  const handleRequestRefresh = useCallback(async () => {
    if (refreshImportIdRef.current || refreshImportStartingRef.current) return;
    const provider = report?.import_provider;
    if (!provider) return;

    refreshImportStartingRef.current = true;
    try {
      const { importId } = await startSavedCredentialReportImport(provider);
      setRefreshImportId(importId);
    } catch (error) {
      refreshImportStartingRef.current = false;
      const message = getImportStartErrorMessage(error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (
        status === 404
        || status === 400
        || (typeof detail === 'string' && detail.toLowerCase().includes('credential'))
      ) {
        setReimportPreferAutoStart(true);
        setShowReimportModal(true);
      } else {
        toast.error(message);
      }
    }
  }, [report?.import_provider]);

  const handleImportRetry = useCallback(() => {
    setRefreshImportId(null);
    refreshImportIdRef.current = null;
    refreshImportStartingRef.current = false;
    window.setTimeout(() => {
      handleRequestRefresh();
    }, 0);
  }, [handleRequestRefresh]);

  const handleImportComplete = useCallback(async (result) => {
    const provider = report?.import_provider;
    const priorReportId = report?.report_id;
    const completedReportId = result?.report_id;

    if (provider) {
      markRefreshHandledAfterCompletion(provider, priorReportId, completedReportId);
    }

    refreshImportStartingRef.current = false;
    setRefreshImportId(null);

    await refreshData();
    if (completedReportId) {
      await selectReport(completedReportId);
    }
    await fetchAvailableReports();
  }, [report?.import_provider, report?.report_id, refreshData, selectReport, fetchAvailableReports]);

  const handleCardNeedCredentials = useCallback(() => {
    setReimportPreferAutoStart(true);
    setShowReimportModal(true);
  }, []);

  const handleCardUpload = useCallback(() => {
    navigate('/credit-report/import');
  }, [navigate]);

  const importSourceDisplay = useMemo(
    () => (report ? getReportSourceDisplay(report, t) : null),
    [report, t],
  );

  const canCredentialReimport = useMemo(
    () => normalizeReportOrigin(report)?.method === 'credential_import' && Boolean(report?.import_provider),
    [report],
  );

  const hasValidParsedReport = useMemo(
    () => hasValidParsedCreditReport(report),
    [report]
  );

  const showIncompleteImportStatus = Boolean(report) && !hasValidParsedReport;

  // Redirect to import if no report
  useEffect(() => {
    if (!contextLoading && !hasReport) {
      navigate('/credit-report/import');
    }
  }, [contextLoading, hasReport, navigate]);

  const loading = contextLoading;

  const fetchCreditReport = async () => {
    // Use context refresh instead of local fetch
    await refreshData();
  };

  // Handle report history selection change
  const handleReportHistoryChange = useCallback((reportId) => {
    // Toast notification when switching reports
    toast.success('Report loaded', {
      description: 'Credit report data has been updated.',
    });
  }, []);

  // Clean tradelines - filter out parser noise
  const cleanTradelines = useMemo(() => {
    if (!report?.tradelines) return [];
    const valid = report.tradelines.filter(isValidTradeline);
    logNegativeClassificationDebug(valid);
    return valid.map((tradeline) => ({
      ...tradeline,
      is_negative: isNegativeAccount(tradeline),
    }));
  }, [report?.tradelines]);

  const strictNegativeCountsByBureau = useMemo(() => {
    const counts = { transunion: 0, experian: 0, equifax: 0 };
    cleanTradelines.forEach((tradeline) => {
      if (!tradeline.is_negative) return;
      const bureauKeys = new Set();
      [tradeline.bureaus_reported, tradeline.bureaus].forEach((values) => {
        if (Array.isArray(values)) {
          values.forEach((value) => {
            const normalized = normalizeBureauKey(value);
            if (normalized) bureauKeys.add(normalized);
          });
        }
      });
      [tradeline.bureau_code, tradeline.bureau].forEach((value) => {
        const normalized = normalizeBureauKey(value);
        if (normalized) bureauKeys.add(normalized);
      });
      bureauKeys.forEach((bureauKey) => {
        counts[bureauKey] += 1;
      });
    });
    return counts;
  }, [cleanTradelines]);

  // Filter tradelines based on main filter, tab, bureau, and search
  const filteredTradelines = useMemo(() => {
    let filtered = cleanTradelines;

    // Main filter (primary)
    switch (mainFilter) {
      case 'negative':
        filtered = filtered.filter(t => t.is_negative);
        break;
      case 'disputable':
        filtered = filtered.filter(t => t.ai_risk_tag === 'DISPUTABLE' || t.dispute_eligibility === 'High' || t.dispute_eligibility === 'Medium');
        break;
      case 'collections':
        filtered = filtered.filter(t => {
          // Check multiple indicators for collections
          const isCollection = t.is_collection === true;
          const classificationHasCollection = (t.classification || '').toLowerCase().includes('collection');
          const statusHasCollection = (t.account_status || t.status || '').toLowerCase().includes('collection');
          const accountTypeHasCollection = (t.account_type || '').toLowerCase().includes('collection');
          return isCollection || classificationHasCollection || statusHasCollection || accountTypeHasCollection;
        });
        break;
      case 'chargeoffs':
        filtered = filtered.filter(t => {
          // Check multiple indicators for charge-offs
          const isChargeOff = t.is_charge_off === true;
          const statusHasChargeOff = (t.account_status || t.status || '').toLowerCase().includes('chargeoff') || 
                                     (t.account_status || t.status || '').toLowerCase().includes('charge-off') ||
                                     (t.account_status || t.status || '').toLowerCase().includes('charge off');
          const classificationHasChargeOff = (t.classification || '').toLowerCase().includes('chargeoff') ||
                                              (t.classification || '').toLowerCase().includes('charge-off') ||
                                              (t.classification || '').toLowerCase().includes('charge off');
          return isChargeOff || statusHasChargeOff || classificationHasChargeOff;
        });
        break;
      case 'late_payments':
        filtered = filtered.filter(t => t.has_late_payments || (t.payment_status || '').toLowerCase().includes('late'));
        break;
      case 'open':
        filtered = filtered.filter(t => (t.account_status || t.status || '').toLowerCase() === 'open');
        break;
      case 'closed':
        filtered = filtered.filter(t => (t.account_status || t.status || '').toLowerCase() === 'closed');
        break;
      default:
        // 'all' - no filter
        break;
    }

    // Tab filter (secondary, only for potentially_negative tab)
    // Skip the is_negative filter if a specific filter is already selected
    // because collections, chargeoffs, etc. are inherently negative items
    if (activeTab === 'potentially_negative' && mainFilter === 'all') {
      filtered = filtered.filter(t => t.is_negative);
    }
    // Note: public_records, personal_info, inquiries tabs don't show tradelines

    // Bureau filter — match against reported_bureaus / bureau_data, not only singular bureau
    if (selectedBureau !== 'all') {
      filtered = filtered.filter(t => tradelineMatchesBureauFilter(t, selectedBureau));
    }

    // Search filter - use canonical field with fallback
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        (t.account_name || t.creditor_name || '').toLowerCase().includes(query) ||
        (t.account_type || '').toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [cleanTradelines, mainFilter, activeTab, selectedBureau, searchQuery]);

  // ============================================
  // SELECT-ALL CHECKBOX STATE & HANDLERS
  // ============================================
  
  // Compute select-all state for accounts (based on visible/filtered tradelines)
  const accountsSelectAllState = useMemo(() => {
    if (!filteredTradelines || filteredTradelines.length === 0) {
      return { checked: false, indeterminate: false };
    }
    
    const selectedCount = filteredTradelines.filter(t => 
      isItemSelected(CATEGORIES.ACCOUNTS, t.tradeline_id)
    ).length;
    
    if (selectedCount === 0) {
      return { checked: false, indeterminate: false };
    } else if (selectedCount === filteredTradelines.length) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  }, [filteredTradelines, isItemSelected, CATEGORIES]);
  
  // Handle select-all toggle for accounts
  const handleSelectAllAccounts = useCallback(() => {
    if (!filteredTradelines || filteredTradelines.length === 0) return;
    
    const allSelected = filteredTradelines.every(t => 
      isItemSelected(CATEGORIES.ACCOUNTS, t.tradeline_id)
    );
    
    if (allSelected) {
      // Deselect all visible rows
      filteredTradelines.forEach(tradeline => {
        if (isItemSelected(CATEGORIES.ACCOUNTS, tradeline.tradeline_id)) {
          removeItem(CATEGORIES.ACCOUNTS, tradeline.tradeline_id);
        }
      });
    } else {
      // Select all visible rows
      filteredTradelines.forEach(tradeline => {
        if (!isItemSelected(CATEGORIES.ACCOUNTS, tradeline.tradeline_id)) {
          addItem(CATEGORIES.ACCOUNTS, tradeline);
        }
      });
    }
  }, [filteredTradelines, isItemSelected, addItem, removeItem, CATEGORIES]);

  const handleViewDetails = (tradeline) => {
    setSelectedTradeline(tradeline);
    setShowDetailModal(true);
  };

  const handleAddDispute = (tradeline) => {
    // Route directly to Dispute Wizard with item pre-selected
    // The wizard will handle the item context and show recommendations
    navigate('/dispute-wizard/factual/select-items', { 
      state: { 
        preselectedItem: {
          type: 'account',
          tradeline,
          source: 'credit_report',
          bureauScope: ['TU', 'EX', 'EQ'].filter(b => 
            tradeline[b.toLowerCase()] || tradeline[`${b.toLowerCase()}_reported`]
          ),
        }
      } 
    });
  };

  // Handle dispute request for accounts that need caution warning
  const handleDisputeWithCaution = (tradeline) => {
    setCautionTradeline(tradeline);
    setShowCautionModal(true);
  };

  // Handle proceeding with dispute after caution acknowledgment
  const handleCautionProceed = () => {
    setShowCautionModal(false);
    if (cautionTradeline) {
      // Route to Dispute Wizard with item context
      navigate('/dispute-wizard/factual/select-items', { 
        state: { 
          preselectedItem: {
            type: 'account',
            tradeline: cautionTradeline,
            source: 'credit_report',
            bureauScope: ['TU', 'EX', 'EQ'].filter(b => 
              cautionTradeline[b.toLowerCase()] || cautionTradeline[`${b.toLowerCase()}_reported`]
            ),
          }
        } 
      });
    }
    setCautionTradeline(null);
  };

  // Handle closing caution modal
  const handleCautionClose = () => {
    setShowCautionModal(false);
    setCautionTradeline(null);
  };

  // Toggle bureau selection for dispute
  const toggleBureauSelection = (tradelineId, bureau) => {
    setSelectedForDispute(prev => {
      const current = prev[tradelineId] || {};
      return {
        ...prev,
        [tradelineId]: {
          ...current,
          [bureau]: !current[bureau]
        }
      };
    });
  };

  // Check if bureau is selected for a tradeline
  const isBureauSelected = (tradelineId, bureau) => {
    return selectedForDispute[tradelineId]?.[bureau] || false;
  };

  // Dispute selected bureau items
  const handleDisputeSelected = (tradeline) => {
    const selectedBureaus = selectedForDispute[tradeline.tradeline_id] || {};
    const bureausToDispute = Object.entries(selectedBureaus).filter(([_, selected]) => selected).map(([bureau]) => bureau);
    
    if (bureausToDispute.length === 0) {
      toast.error('Select at least one bureau to dispute');
      return;
    }
    
    // Route to Dispute Wizard with selected bureaus context
    navigate('/dispute-wizard/factual/select-items', { 
      state: { 
        preselectedItem: {
          type: 'account',
          tradeline,
          source: 'credit_report',
          bureauScope: bureausToDispute,
        }
      } 
    });
  };

  // Calculate derived average (clearly labeled) — only from real parsed bureau scores
  const derivedAverage = useMemo(() => {
    if (!report || !hasValidParsedReport) return null;
    const scores = CREDIT_REPORT_BUREAUS
      .map((bureau) => getBureauScoreValue(report[bureau]))
      .filter((score) => !isPlaceholderCreditScore(score) && score >= 301 && score <= 850);
    if (scores.length === 0) return null;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  }, [report, hasValidParsedReport]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="credit-report-loading">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-48 w-full rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-14 w-full rounded-xl" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="credit-report-page">
      {/* Import Source Banner - Card styled */}
      {(report?.import_provider || report?.import_source) && importSourceDisplay && (
        <CredentialImportStatusCardBoundary
          report={activeReportData || report}
          theme={theme}
          sourceDisplay={importSourceDisplay}
          canCredentialReimport={canCredentialReimport}
          activeImportId={refreshImportId}
          onUpload={handleCardUpload}
          onRequestRefresh={handleRequestRefresh}
          onNeedCredentials={handleCardNeedCredentials}
        />
      )}

      {/* Report History Selector - Compact dropdown for switching between reports */}
      <section 
        className="flex items-center gap-4"
        data-testid="report-history-section"
      >
        <ReportHistorySelector 
          onReportChange={handleReportHistoryChange}
        />
      </section>

      {/* Page Header */}
      <section className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="flex-shrink-0">
          <h1 
            className="text-lg sm:text-xl font-black tracking-tight"
            style={{ color: theme.text.primary, letterSpacing: '-0.03em' }}
          >
            {t('creditReport.title')}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Button 
            variant="outline" 
            onClick={() => navigate(`/credit-report/analysis?report_id=${report?.report_id}`)}
            className="h-9 sm:h-11 px-3 sm:px-4 font-bold text-xs sm:text-sm"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.1))',
              border: `1px solid ${theme.green[700]}40`,
              borderRadius: '12px',
              color: theme.green[700],
            }}
            disabled={!report?.report_id || !hasValidParsedReport}
            data-testid="report-analysis-btn"
          >
            <FileText size={14} className="mr-1.5 sm:mr-2" />
            <span className="hidden xs:inline">{t('creditReport.pdfAnalysis')}</span>
            <span className="xs:hidden">{t('creditReport.analysis')}</span>
          </Button>
          {normalizeReportOrigin(report)?.method !== 'credential_import' && (
          <Button 
            variant="outline" 
            onClick={fetchCreditReport} 
            className="h-9 sm:h-11 px-3 sm:px-4 font-bold text-xs sm:text-sm"
            style={{
              background: theme.surface.s2,
              border: `1px solid ${theme.border.b2}`,
              borderRadius: '12px',
              color: theme.green[700],
            }}
            data-testid="refresh-report"
          >
            <RefreshCw size={14} className="mr-1.5 sm:mr-2" />
            {t('creditReport.refresh')}
          </Button>
          )}
        </div>
      </section>

      {showIncompleteImportStatus && (
        <ImportIncompleteStatusCard
          theme={theme}
          onRetryImport={() => {
            const origin = normalizeReportOrigin(report);
            if (origin?.method === 'credential_import' && report?.import_provider) {
              setShowReimportModal(true);
            } else {
              navigate('/credit-report/import');
            }
          }}
          onUploadReport={() => navigate('/credit-report/import')}
        />
      )}

      {hasValidParsedReport && (
      <>
      {/* Bureau Score Cards Grid */}
      <section 
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 px-2 sm:px-0"
        data-testid="bureau-scores-section"
      >
        <BureauScoreCard
          bureau="TransUnion"
          data={report?.transunion}
          theme={theme}
          reportOrigin={normalizeReportOrigin(report)}
          strictDerogatoryCount={strictNegativeCountsByBureau.transunion}
          report={activeReportData || report}
        />
        <BureauScoreCard
          bureau="Experian"
          data={report?.experian}
          theme={theme}
          reportOrigin={normalizeReportOrigin(report)}
          strictDerogatoryCount={strictNegativeCountsByBureau.experian}
          report={activeReportData || report}
        />
        <BureauScoreCard
          bureau="Equifax"
          data={report?.equifax}
          theme={theme}
          reportOrigin={normalizeReportOrigin(report)}
          strictDerogatoryCount={strictNegativeCountsByBureau.equifax}
          report={activeReportData || report}
        />
      </section>

      {/* Derived Average Score Card */}
      {derivedAverage && (
        <section 
          className="relative overflow-hidden flex items-center justify-between gap-4 py-4 px-4"
          style={{
            background: theme.card.bg,
            border: `1px solid ${theme.card.border}`,
            borderRadius: '22px',
            boxShadow: theme.card.shadow,
            backdropFilter: 'blur(16px) saturate(140%)',
          }}
          data-testid="derived-average"
        >
          <div style={{ position: 'absolute', inset: 0, background: theme.card.innerGlow, pointerEvents: 'none' }} />
          
          <div className="flex items-start gap-3 relative z-10">
            <div 
              className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: theme.shield.bg }}
            >
              <Info size={14} style={{ color: theme.shield.color }} fontWeight={800} />
            </div>
            <div>
              <h4 className="text-base font-extrabold mb-1" style={{ color: theme.text.primary }}>
                {t('creditReport.derivedAverageScore')}
              </h4>
              <p className="text-xs" style={{ color: theme.text.muted }}>
                {t('creditReport.derivedAverageFormula')}
              </p>
            </div>
          </div>
          <div 
            className="relative z-10 text-xl font-black"
            style={{ color: theme.text.primary }}
          >
            {derivedAverage}
          </div>
        </section>
      )}

      {/* Report Section Tabs */}
      <SectionTabs activeTab={activeTab} onTabChange={handleSectionTabChange} categoryCounts={categoryCounts} theme={theme} />

      {/* Filter Shell - Premium glass surface matching HTML reference */}
      {(activeTab === 'potentially_negative' || activeTab === 'accounts') && (
        <section 
          className="filter-shell relative overflow-hidden"
          style={{
            background: theme.surface.s1,
            border: `1px solid ${theme.border.b1}`,
            borderRadius: '18px',
            boxShadow: theme.card.shadow,
            backdropFilter: 'blur(16px) saturate(140%)',
            WebkitBackdropFilter: 'blur(16px) saturate(140%)',
            padding: '12px 14px',
          }}
          data-testid="filter-shell"
        >
          {/* Inner glow overlay */}
          <div style={{ position: 'absolute', inset: 0, background: theme.card.innerGlow, pointerEvents: 'none', borderRadius: '18px' }} />
          
          <div 
            className="relative z-10 flex flex-wrap items-center gap-2.5"
          >
            {/* Main Filter */}
            <Select value={mainFilter} onValueChange={setMainFilter}>
              <SelectTrigger 
                className="h-10 px-3 font-semibold text-sm min-w-[150px]"
                style={{
                  background: theme.input.bg,
                  border: `1px solid ${theme.input.border}`,
                  borderRadius: '12px',
                  color: theme.text.secondary,
                  backdropFilter: 'blur(12px)',
                }}
                data-testid="main-filter"
              >
                <Filter size={14} className="mr-2 flex-shrink-0" style={{ color: theme.icon.neutral }} />
                <SelectValue placeholder={t('creditReport.allAccounts')} />
              </SelectTrigger>
              <SelectContent>
                {mainFilters.map(f => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Bureau Filter */}
            <Select value={selectedBureau} onValueChange={setSelectedBureau}>
              <SelectTrigger 
                className="h-10 px-3 font-semibold text-sm min-w-[130px]"
                style={{
                  background: theme.input.bg,
                  border: `1px solid ${theme.input.border}`,
                  borderRadius: '12px',
                  color: theme.text.secondary,
                  backdropFilter: 'blur(12px)',
                }}
                data-testid="bureau-filter"
              >
                <Database size={14} className="mr-2 flex-shrink-0" style={{ color: theme.icon.neutral }} />
                <SelectValue placeholder={t('creditReport.allBureaus')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('creditReport.allBureaus')}</SelectItem>
                <SelectItem value="Equifax">Equifax</SelectItem>
                <SelectItem value="Experian">Experian</SelectItem>
                <SelectItem value="TransUnion">TransUnion</SelectItem>
              </SelectContent>
            </Select>

            {/* Search Input */}
            <div 
              className="flex items-center h-10 px-3 flex-1 min-w-[200px]"
              style={{
                background: theme.input.bg,
                border: `1px solid ${theme.input.border}`,
                borderRadius: '12px',
                backdropFilter: 'blur(12px)',
              }}
            >
              <Search size={15} className="flex-shrink-0" style={{ color: theme.icon.neutral }} />
              <input
                type="text"
                placeholder={t('creditReport.searchAccounts')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent border-none outline-none ml-2.5 text-sm"
                style={{ color: theme.text.primary }}
                data-testid="search-accounts"
              />
            </div>

            {/* Refresh button */}
            <button 
              onClick={fetchCreditReport}
              className="h-11 w-12 flex items-center justify-center"
              style={{
                background: theme.input.bg,
                border: `1px solid ${theme.input.border}`,
                borderRadius: '14px',
                color: theme.text.secondary,
              }}
            >
              <RefreshCw size={16} />
            </button>

            {/* Count display */}
            <div 
              className="text-sm text-right pr-1.5"
              style={{ color: theme.text.muted }}
            >
              {activeTab === 'potentially_negative' && (
                <span>{filteredTradelines.length} potentially negative item(s)</span>
              )}
              {activeTab === 'accounts' && (
                <span>{filteredTradelines.length} account(s)</span>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Tab Content - Table Card with Premium Glass Surface */}
      <section 
        className="table-container relative overflow-hidden"
        style={{
          background: theme.table.container,
          border: `1px solid ${theme.card.border}`,
          borderRadius: '22px',
          boxShadow: theme.card.shadow,
          backdropFilter: 'blur(16px) saturate(140%)',
          WebkitBackdropFilter: 'blur(16px) saturate(140%)',
        }}
        data-testid="table-section"
      >
        {/* Inner glow overlay */}
        <div style={{ position: 'absolute', inset: 0, background: theme.card.innerGlow, pointerEvents: 'none', borderRadius: '22px' }} />
        
        {/* Potentially Negative Tab */}
        {activeTab === 'potentially_negative' && (
          <div data-testid="tab-content-potentially-negative" className="relative z-10">
            {filteredTradelines.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-max">
                  <thead>
                    <tr style={{ background: theme.table.header }}>
                      <th 
                        className="text-center py-3 px-3 text-xs font-bold uppercase tracking-wide w-10"
                        style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}
                      >
                        <Checkbox 
                          className="data-[state=checked]:bg-emerald-600 data-[state=indeterminate]:bg-emerald-600"
                          checked={accountsSelectAllState.checked}
                          indeterminate={accountsSelectAllState.indeterminate}
                          onCheckedChange={handleSelectAllAccounts}
                          data-testid="select-all-negative-header"
                        />
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>{t('creditReport.columnAccount')}</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>{t('creditReport.columnStatus')}</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>{t('creditReport.columnBalance')}</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>{t('creditReport.columnDates')}</th>
                      <th className="text-center py-3 px-2 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>TU</th>
                      <th className="text-center py-3 px-2 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>EX</th>
                      <th className="text-center py-3 px-2 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>EQ</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>{t('creditReport.columnAiTags')}</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>{t('creditReport.columnActions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTradelines.map((tradeline, idx) => (
                      <TradelineRow
                        key={tradeline.tradeline_id}
                        tradeline={tradeline}
                        onViewDetails={handleViewDetails}
                        onAddDispute={handleAddDispute}
                        onDisputeWithCaution={handleDisputeWithCaution}
                        onToggleBureau={toggleBureauSelection}
                        selectedBureaus={selectedForDispute[tradeline.tradeline_id]}
                        onToggleTray={(item) => handleToggleTrayItem(item, CATEGORIES.ACCOUNTS)}
                        isTraySelected={isItemSelected(CATEGORIES.ACCOUNTS, tradeline.tradeline_id)}
                        theme={theme}
                        rowIndex={idx}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                icon={CheckCircle}
                title="No Potentially Negative Items"
                description="Great news! No potentially negative items found in your report."
                theme={theme}
                isPositive={true}
              />
            )}
          </div>
        )}

        {/* Accounts Tab */}
        {activeTab === 'accounts' && (
          <div data-testid="tab-content-accounts" className="relative z-10">
            {filteredTradelines.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full min-w-max">
                  <thead>
                    <tr style={{ background: theme.table.header }}>
                      <th 
                        className="text-center py-3 px-3 text-xs font-bold uppercase tracking-wide w-10"
                        style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}
                      >
                        <Checkbox 
                          className="data-[state=checked]:bg-emerald-600 data-[state=indeterminate]:bg-emerald-600"
                          checked={accountsSelectAllState.checked}
                          indeterminate={accountsSelectAllState.indeterminate}
                          onCheckedChange={handleSelectAllAccounts}
                          data-testid="select-all-accounts-header"
                        />
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>{t('creditReport.columnAccount')}</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>{t('creditReport.columnStatus')}</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>{t('creditReport.columnBalance')}</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>{t('creditReport.columnDates')}</th>
                      <th className="text-center py-3 px-2 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>TU</th>
                      <th className="text-center py-3 px-2 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>EX</th>
                      <th className="text-center py-3 px-2 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>EQ</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>{t('creditReport.columnAiTags')}</th>
                      <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide" style={{ color: theme.text.muted, borderBottom: `1px solid ${theme.table.border}` }}>{t('creditReport.columnActions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTradelines.map((tradeline, idx) => (
                      <TradelineRow
                        key={tradeline.tradeline_id}
                        tradeline={tradeline}
                        onViewDetails={handleViewDetails}
                        onAddDispute={handleAddDispute}
                        onDisputeWithCaution={handleDisputeWithCaution}
                        onToggleBureau={toggleBureauSelection}
                        selectedBureaus={selectedForDispute[tradeline.tradeline_id]}
                        onToggleTray={(item) => handleToggleTrayItem(item, CATEGORIES.ACCOUNTS)}
                        isTraySelected={isItemSelected(CATEGORIES.ACCOUNTS, tradeline.tradeline_id)}
                        theme={theme}
                        rowIndex={idx}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                icon={CreditCard}
                title="No Accounts Found"
                description="No accounts match your current filters."
                theme={theme}
              />
            )}
          </div>
        )}

        {/* Public Records Tab */}
        {activeTab === 'public_records' && (
          <div data-testid="tab-content-public-records" className="relative z-10 p-6">
            <PublicRecordsTable 
              publicRecords={report?.public_records || []}
              bureauCounts={{
                transunion: report?.transunion?.public_records_count ?? 0,
                experian: report?.experian?.public_records_count ?? 0,
                equifax: report?.equifax?.public_records_count ?? 0
              }}
              theme={theme}
            />
          </div>
        )}

        {/* Personal Information Tab - Enhanced with structured table and bulk actions */}
        {activeTab === 'personal_info' && (
          <div data-testid="tab-content-personal-info" className="relative z-10 p-6">
            <PersonalInfoTable 
              personalInfo={report?.personal_info || {}}
              addresses={report?.addresses || []}
              theme={theme}
            />
          </div>
        )}

        {/* Inquiries Tab - Enhanced with structured table and bulk actions */}
        {activeTab === 'inquiries' && (
          <div data-testid="tab-content-inquiries" className="relative z-10 p-6">
            <InquiriesTable 
              inquiries={report?.inquiries || []}
              tradelines={cleanTradelines}
              theme={theme}
              bureauCounts={{
                transunion: report?.transunion?.inquiries_count ?? 0,
                experian: report?.experian?.inquiries_count ?? 0,
                equifax: report?.equifax?.inquiries_count ?? 0
              }}
            />
          </div>
        )}

        {/* Creditor Contacts Tab - Grid layout with themed cards */}
        {activeTab === 'creditor_contacts' && (
          <div data-testid="tab-content-creditor-contacts" className="relative z-10 p-6">
            {/* Section Header */}
            <div className="mb-5">
              <h3 
                className="text-lg font-bold mb-1"
                style={{ color: theme.text.primary }}
              >
                Creditor Contact Information
              </h3>
              <p 
                className="text-sm"
                style={{ color: theme.text.muted }}
              >
                Use this information for dispute letters and direct communication with creditors.
              </p>
            </div>
            
            {report?.creditor_contacts?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {report.creditor_contacts.map((contact, idx) => (
                  <div 
                    key={idx} 
                    className="contact-card p-4 rounded-xl transition-all duration-200"
                    style={{ 
                      background: theme.surface.s2,
                      border: `1px solid ${theme.border.b2}`,
                    }}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon box with emerald color for utility */}
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ 
                          background: theme.iconBox.bg,
                          border: `1px solid ${theme.iconBox.border}`,
                        }}
                      >
                        <Building size={18} style={{ color: theme.icon.primary }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p 
                          className="font-semibold truncate"
                          style={{ color: theme.contact.title }}
                        >
                          {contact.name || contact.raw_name}
                        </p>
                        
                        {/* Address */}
                        {(contact.full_address || contact.city) && (
                          <div 
                            className="flex items-start gap-1.5 mt-2 text-sm"
                            style={{ color: theme.contact.meta }}
                          >
                            <MapPin size={14} className="mt-0.5 flex-shrink-0" style={{ color: theme.icon.neutral }} />
                            <span className="break-words">
                              {contact.full_address || `${contact.city}, ${contact.state} ${contact.zip || ''}`}
                            </span>
                          </div>
                        )}
                        
                        {/* Phone */}
                        {contact.phone && (
                          <div className="flex items-center gap-1.5 mt-1.5 text-sm">
                            <Phone size={14} className="flex-shrink-0" style={{ color: theme.icon.neutral }} />
                            <a 
                              href={`tel:${contact.phone}`} 
                              className="hover:underline"
                              style={{ color: theme.action.primary }}
                            >
                              {contact.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Phone}
                title="No Creditor Contacts Found"
                description="Creditor contact information was not extracted from your report."
                theme={theme}
              />
            )}
          </div>
        )}
      </section>
      </>
      )}

      {/* Debug Panel removed - not shown to users */}

      {/* Detail Modal - Pass creditor contacts for account-level contact lookup */}
      <TradelineDetailModal
        tradeline={selectedTradeline}
        creditorContacts={report?.creditor_contacts || []}
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        buildBureauAccount={buildBureauAccount}
        darkMode={resolvedTheme === 'dark'}
      />

      {/* Positive Account Warning Modal */}
      <PositiveAccountWarning
        open={showCautionModal}
        onClose={handleCautionClose}
        onProceed={handleCautionProceed}
        accountName={cautionTradeline?.account_name || cautionTradeline?.creditor_name}
      />

      {/* Request Report Modal (Secondary Entry Point) */}
      <RequestReportModal
        isOpen={showRequestReportModal}
        onClose={() => setShowRequestReportModal(false)}
        onSuccess={(result) => {
          toast.success(`Request sent to ${result.source_display_name}!`);
        }}
      />
      
      {/* Re-import Modal (Credential Import Flow) */}
      <ReimportModal
        isOpen={showReimportModal}
        onClose={() => {
          setShowReimportModal(false);
          setReimportPreferAutoStart(false);
        }}
        report={report}
        theme={theme}
        preferAutoStart={reimportPreferAutoStart}
        onImportComplete={() => {
          setReimportPreferAutoStart(false);
          refreshData();
        }}
      />

      <ProviderRefreshImportDialog
        isOpen={Boolean(refreshImportId)}
        importId={refreshImportId}
        onComplete={handleImportComplete}
        onClose={handleImportDismissed}
        onRetry={handleImportRetry}
      />
    </div>
  );
};

export default CreditReport;
